import React, { createContext, useContext, useState, useCallback } from 'react'

const ToastContext = createContext(null)

// Acentos subtis (on-brand) — a cor é só um detalhe (barra + ícone), não o fundo.
const TIPOS = {
  erro:    { acento: '#C0392B', glifo: '!', rotulo: 'Erro' },
  sucesso: { acento: '#3E7C5A', glifo: '✓', rotulo: 'Feito' },
  aviso:   { acento: 'var(--cor-ambar)', glifo: '!', rotulo: 'Atenção' },
}

let idSeq = 0

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])

  const remover = useCallback((id) => {
    setToasts(ts => ts.filter(t => t.id !== id))
  }, [])

  const mostrar = useCallback((mensagem, tipo = 'erro', duracao) => {
    if (!mensagem) return
    const id = ++idSeq
    const dur = duracao ?? (tipo === 'erro' ? 6500 : 4000)
    setToasts(ts => [...ts, { id, mensagem, tipo }])
    if (dur > 0) setTimeout(() => remover(id), dur)
    return id
  }, [remover])

  // API estável (não muda entre renders)
  const [toast] = useState(() => ({
    erro:    (m, d) => mostrar(m, 'erro', d),
    sucesso: (m, d) => mostrar(m, 'sucesso', d),
    aviso:   (m, d) => mostrar(m, 'aviso', d),
  }))

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div
        aria-live="polite"
        style={{
          position: 'fixed',
          top: 'max(16px, env(safe-area-inset-top))',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 9999,
          width: 'min(420px, calc(100vw - 24px))',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          pointerEvents: 'none',
        }}
      >
        {toasts.map(t => (
          <Toast key={t.id} {...t} onFechar={() => remover(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

function Toast({ mensagem, tipo, onFechar }) {
  const cfg = TIPOS[tipo] || TIPOS.erro
  return (
    <div
      role="status"
      style={{
        pointerEvents: 'auto',
        position: 'relative',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        background: 'var(--cor-branco)',
        border: '1px solid var(--cor-borda)',
        borderRadius: 'var(--raio-lg)',
        boxShadow: '0 8px 28px rgba(44, 26, 14, 0.16)',
        padding: '14px 14px 14px 16px',
        overflow: 'hidden',
        animation: 'toastIn 0.28s var(--ease) both',
      }}
    >
      {/* barra de acento */}
      <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', background: cfg.acento }} />

      {/* ícone */}
      <span aria-hidden="true" style={{
        flexShrink: 0,
        width: '22px', height: '22px', marginTop: '1px',
        borderRadius: '50%',
        background: cfg.acento,
        color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '13px', fontWeight: '700', lineHeight: 1,
      }}>
        {cfg.glifo}
      </span>

      {/* mensagem */}
      <div style={{ flex: 1, minWidth: 0, fontSize: '14px', color: 'var(--cor-texto)', lineHeight: 1.45, paddingTop: '1px' }}>
        {mensagem}
      </div>

      {/* fechar */}
      <button
        onClick={onFechar}
        aria-label="Fechar"
        style={{
          flexShrink: 0,
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--cor-texto-suave)', fontSize: '18px', lineHeight: 1,
          padding: '0 2px', marginTop: '-1px',
        }}
      >
        ×
      </button>
    </div>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast deve ser usado dentro de ToastProvider')
  return ctx
}
